/**
 * Seats on a team, for every owner, regardless of subscription or purchased
 * quantity (decision, Ben, 2026-09-22). Seats used to be
 * `stripeQuantity * 10`; checkout is now pinned to quantity 1 and a seat
 * add-on product comes later, on the new stack.
 *
 * The owner is not a member of their own `teamMembers` array, so this is ten
 * members alongside the owner — unchanged from the previous behaviour.
 *
 * `functions/index.js` keeps its own copy (`TEAM_MAX_SEATS`) because Cloud
 * Functions deploy separately and cannot import from `src/`. Change both.
 */
export const TEAM_MAX_SEATS = 10;

/**
 * Product catalog — single source of truth for tracking metadata.
 * Uses the same REACT_APP_*_PRICE env vars that PricingSectionCommon.js uses,
 * so tracking values stay in sync with what customers see.
 */
export const productCatalog = {
  sbm:   { name: 'Stairs & Buckets Monthly',          product_line: 'sb',    billing_type: 'monthly',  value: parseFloat(process.env.REACT_APP_SBM_PRICE) || 89.99 },
  sby:   { name: 'Stairs & Buckets Annual',            product_line: 'sb',    billing_type: 'annual',   value: parseFloat(process.env.REACT_APP_SBY_PRICE) || 899.99 },
  sbm1:  { name: 'Stairs & Buckets Monthly',           product_line: 'sb',    billing_type: 'monthly',  value: parseFloat(process.env.REACT_APP_SBM_PRICE) || 89.99 },
  tpm:   { name: 'Agency Training Monthly',            product_line: 'tp',    billing_type: 'monthly',  value: parseFloat(process.env.REACT_APP_TPM_PRICE) || 399.99 },
  tp:    { name: 'Agency Training Annual',             product_line: 'tp',    billing_type: 'annual',   value: parseFloat(process.env.REACT_APP_TP_PRICE) || 3999.99 },
  tpl:   { name: 'Agency Training Lifetime',           product_line: 'tp',    billing_type: 'lifetime', value: 3999.99 },
  proc:  { name: 'Million Dollar Tool Kit',            product_line: 'proc',  billing_type: 'annual',   value: 499.99 },
  procm: { name: 'Tool Kit Monthly',                   product_line: 'proc',  billing_type: 'monthly',  value: 49.99 },
  life:  { name: 'Lifetime Membership',                product_line: 'life',  billing_type: 'lifetime', value: 1499.99 },
  shuff: { name: 'Service Webinars',                   product_line: 'shuff', billing_type: 'annual',   value: 199.99 },
  proc1: { name: 'Sales Process One: Medicare',        product_line: 'proc',  billing_type: 'one_time', value: 0 },
  proc2: { name: 'Sales Process Two: Premium Life',    product_line: 'proc',  billing_type: 'one_time', value: 0 },
  proc3: { name: 'Sales Process Three: Finding Money', product_line: 'proc',  billing_type: 'one_time', value: 0 },
  proc4: { name: 'Sales Process Four: Protect',        product_line: 'proc',  billing_type: 'one_time', value: 0 },
  proc5: { name: 'Sales Process Five: Understanding',  product_line: 'proc',  billing_type: 'one_time', value: 0 },
  proc6: { name: 'Sales Process Six: Liability Wall',  product_line: 'proc',  billing_type: 'one_time', value: 0 },
  proc7: { name: 'Medicare Slide Show',                product_line: 'proc',  billing_type: 'one_time', value: 0 },
  tp1:   { name: 'Million Dollar Manual',              product_line: 'tp',    billing_type: 'one_time', value: 0 },
  tp2:   { name: 'Staff Handbook',                     product_line: 'tp',    billing_type: 'one_time', value: 0 },
  tp3:   { name: 'Paychecks Never Lie',                product_line: 'tp',    billing_type: 'one_time', value: 0 },
  tp4:   { name: 'Bigger Better Paychecks',            product_line: 'tp',    billing_type: 'one_time', value: 0 },
};

/**
 * Get product metadata for tracking.
 * @param {string} productId - Product key (sbm, tp, proc, etc.)
 * @returns {{ name: string, product_line: string, billing_type: string, value: number } | null}
 */
export function getProductMeta(productId) {
  return productCatalog[productId] || null;
}

// Map our custom plan IDs ("basic", "premium", etc) to Stripe price IDs
const stripePriceIds = {
  sby: process.env.REACT_APP_STRIPE_PRICE_SBY,
  sbm: process.env.REACT_APP_STRIPE_PRICE_SBM,
  sbm1: process.env.REACT_APP_STRIPE_PRICE_SBM1,

  tp: process.env.REACT_APP_STRIPE_PRICE_TP,
  tpm: process.env.REACT_APP_STRIPE_PRICE_TPM,
  tpl: process.env.REACT_APP_STRIPE_PRICE_TPL,
  proc: process.env.REACT_APP_STRIPE_PRICE_PROC,
  procm: process.env.REACT_APP_STRIPE_PRICE_PROCM,
  life: process.env.REACT_APP_STRIPE_PRICE_LIFE,


  proc1: process.env.REACT_APP_STRIPE_PRICE_PROC1,
  proc2: process.env.REACT_APP_STRIPE_PRICE_PROC2,
  proc3: process.env.REACT_APP_STRIPE_PRICE_PROC3,
  proc4: process.env.REACT_APP_STRIPE_PRICE_PROC4,
  proc5: process.env.REACT_APP_STRIPE_PRICE_PROC5,
  proc6: process.env.REACT_APP_STRIPE_PRICE_PROC6,
  proc7: process.env.REACT_APP_STRIPE_PRICE_PROC7,

  tp1: process.env.REACT_APP_STRIPE_PRICE_TP1,
  tp2: process.env.REACT_APP_STRIPE_PRICE_TP2,
  tp3: process.env.REACT_APP_STRIPE_PRICE_TP3,
  tp4: process.env.REACT_APP_STRIPE_PRICE_TP4,
};

// Retired products: excluded from getStripePriceId so they can no longer be
// purchased, but still resolvable by getFriendlyPlanId so existing
// subscribers keep their plan access.
const retiredPriceIds = {
  shuff: process.env.REACT_APP_STRIPE_PRICE_SHUFF,
};

const keyNames = {
  sby: "Million Dollar Stairs and Buckets Start-Up",
  sbm: "The Million Dollar Stairs and Buckets Start-Up Monthly",
  sbm1: "The Million Dollar Stairs and Buckets Start-Up Monthly",
  proc: "Million Dollar Tool Kit",
  tp: "Million Dollar Road Map",
  procm: "Million Dollar Tool Kit Monthly",
  tpm: "Million Dollar Road Map Monthly",
  life: "All Access Million Dollar Membership",
  shuff: "Service Webinars",

  proc1: "Sales Process One: Medicare",
  proc2: "Sales Process Two: Premium Life",
  proc3: "Sales Processs Three: Finding The Money",
  proc4: "Sales Process Four: Protect & Participate",
  proc5: "Sales Process Five: Understanding Money",
  proc6: "Sales Process Six: Liability Wall",
  proc7: "Medicare Slide Show",

  tp1: "Million Dollar Manual",
  tp2: "Staff Handbook",
  tp3: "Paychecks Never Lie",
  tp4: "Bigger Better Paychecks",
}

// Get Stripe priceId
export function getStripePriceId(planId) {
  return stripePriceIds[planId];
}

// Get friendly plan ID ("basic", "premium", etc) by Stripe plan ID
// Used in auth.js to include planId in the user object
export function getFriendlyPlanId(stripePriceId) {
  const allPriceIds = { ...stripePriceIds, ...retiredPriceIds };
  return Object.keys(allPriceIds).find(
    (key) => allPriceIds[key] === stripePriceId
  );
}

export function getPlanName(planId) {
  return keyNames[planId];
}

/**
 * T-011.22: Product ID to onboarding route mapping
 * Single source of truth for Vercel frontend checkout flows
 */
const productOnboardingRoutes = {
  // Agency Training
  tp: '/onboarding/agency-training',
  tpm: '/onboarding/agency-training',

  // Stairs & Buckets
  sby: '/onboarding/stairs-buckets',
  sbm: '/onboarding/stairs-buckets',

  // Tool Kit (goes to dashboard)
  proc: '/dashboard/dashboard',
  procm: '/dashboard/dashboard',

  // Lifetime (goes to dashboard)
  life: '/dashboard/dashboard',
};

/**
 * Get onboarding route for a product
 * @param {string} productId - Product key (tp, tpm, sby, etc.)
 * @returns {string} Onboarding route path
 */
export function getOnboardingRoute(productId) {
  return productOnboardingRoutes[productId] || '/dashboard/dashboard';
}